// src/pages/admin/GateListsAdminPage.js
import React, { useMemo, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { db } from '../../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// Optional: lock this page to a specific admin wallet (uncomment to enforce)
const ALLOWED_PUBLIC_KEY = ''; // e.g. '5Je5czeRdZ6WnbjQDhK8C6vNnYowh7TjchxAE3AyBhZL'

export default function GateListsAdminPage() {
    const { publicKey, connected } = useWallet();
    const [name, setName] = useState('');
    const [gateType, setGateType] = useState('token'); // 'token' | 'collection'
    const [collectionId, setCollectionId] = useState('');
    const [hashListText, setHashListText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    // const [newCompressedCollectionIds, setNewCompressedCollectionIds] = useState([]);

    const isAuthorized = useMemo(() => {
        if (!ALLOWED_PUBLIC_KEY) return true; // no restriction
        return publicKey?.toBase58() === ALLOWED_PUBLIC_KEY;
    }, [publicKey]);

    const parsedHashList = useMemo(() => {
        if (!hashListText.trim()) return null;
        try {
            const json = JSON.parse(hashListText);
            return Array.isArray(json) ? json : null;
        } catch {
            return null;
        }
    }, [hashListText]);

    if (!connected) {
        return (
            <div className="muulti-container">
                <h2>Gate Lists (Admin)</h2>
                <p>Please connect your Solana wallet to continue.</p>
            </div>
        );
    }
    if (!isAuthorized) {
        return (
            <div className="muulti-container">
                <h2>Gate Lists (Admin)</h2>
                <p>Unauthorized: you do not have access to this page.</p>
            </div>
        );
    }

    const handleHashFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const text = String(reader.result || '');
                const arr = JSON.parse(text);
                setHashListText(JSON.stringify(Array.isArray(arr) ? arr : [], null, 2));
            } catch {
                // ignore invalid JSON, keep current textarea content
            }
        };
        reader.readAsText(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage('');

        try {
            const base = {
                name: name.trim(),
                gateType,
                createdAt: serverTimestamp(),
            };
            if (!base.name) throw new Error('Name is required');

            if (gateType === 'token') {
                if (!parsedHashList || parsedHashList.length === 0)
                    throw new Error('Please provide a valid JSON array of token mint IDs.');
                await addDoc(collection(db, 'gateLists'), {
                    ...base,
                    gateType: 'token',
                    hashList: parsedHashList,
                });

            } else if (gateType === 'collection') {
                const id = collectionId.trim();
                if (!id) throw new Error('Collection ID is required.');
                await addDoc(collection(db, 'gateLists'), {
                    ...base,
                    gateType: 'collection',
                    collectionId: id,
                });

            } else if (gateType === 'compressedCollection') {           // ++++++++ INSERT HERE
                const id = collectionId.trim();
                if (!id) throw new Error('Compressed collection ID is required.');
                await addDoc(collection(db, 'gateLists'), {
                    ...base,
                    gateType: 'compressedCollection',
                    compressedCollectionId: id, // the DAS grouping “collection” value
                });                                                              // ++++++++

            } else {
                throw new Error('Unknown gate type');
            }

            setMessage('✅ Gate list saved successfully.');
            setName('');
            setCollectionId('');
            setHashListText('');
        } catch (err) {
            console.error(err);
            setMessage(err?.message || 'Error saving gate list');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="muulti-container">
            <div className="token-gate-container">
                <h1>Create Token Gates for your Muulti Space Content</h1>

                <p style={{ maxWidth: 700, marginBottom: '1rem' }}>
                    Create reusable <strong>gate lists</strong> that your components can reference from the
                    editor controls. Lists can be:
                </p>
                <ul style={{ maxWidth: 700, marginTop: 0 }}>
                    <li><strong>Token-gated</strong>: a JSON array of <em>legacy NFT mint IDs</em>.</li>
                    <li>
                        <strong>Collection-gated</strong>: a single <em>collection ID</em> that works for
                        uncompressed NFTs, MPL-Core assets, and <em>compressed NFTs</em> (via DAS grouping).
                    </li>
                    <li><strong>Compressed Collection</strong>: the DAS grouping <em>collection</em> value for cNFT groups.</li> {/* NEW */}

                </ul>

                <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem', maxWidth: 520 }}>
                    <label>
                        Name:
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </label>

                    <label>
                        Type:
                        <select value={gateType} onChange={(e) => setGateType(e.target.value)}>
                            <option value="token">Token-gated (mint list)</option>
                            <option value="collection">Collection-gated (works for cNFTs)</option>
                            <option value="compressedCollection">Compressed Collection</option>
                        </select>
                    </label>

                    {gateType === 'collection' && (
                        <>
                            <label>
                                Collection ID:
                                <input
                                    type="text"
                                    placeholder="e.g. 3hJ... (DAS grouping: group_key=collection)"
                                    value={collectionId}
                                    onChange={(e) => setCollectionId(e.target.value)}
                                    required
                                />
                            </label>
                            <small style={{ opacity: 0.8 }}>
                                Tip: For compressed NFTs, use the collection value returned by the DAS API in the
                                asset’s <code>grouping</code> array (where <code>group_key</code> is
                                <code> "collection"</code>).
                            </small>
                        </>
                    )}

                    {gateType === 'compressedCollection' && (
                        <>
                            <label>
                                Compressed Collection ID:
                                <input
                                    type="text"
                                    placeholder='DAS grouping "collection" (group_value)'
                                    value={collectionId}
                                    onChange={(e) => setCollectionId(e.target.value)}
                                    required
                                />
                            </label>
                            <small style={{ opacity: 0.8 }}>
                                Tip: Use the <code>group_value</code> from the asset’s <code>grouping</code> where
                                <code> group_key</code> is <code>"collection"</code> (DAS).
                            </small>
                        </>
                    )}


                    {gateType === 'token' && (
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                            <label>
                                Upload Hash List (.json):
                                <input type="file" accept=".json" onChange={handleHashFile} />
                            </label>
                            <label>
                                Hash List (JSON):
                                <textarea
                                    rows={8}
                                    value={hashListText}
                                    onChange={(e) => setHashListText(e.target.value)}
                                    placeholder='e.g. ["mint1","mint2","mint3"]'
                                    required
                                />
                            </label>
                            <small style={{ opacity: 0.8 }}>
                                {parsedHashList
                                    ? `Detected ${parsedHashList.length} mint id${parsedHashList.length === 1 ? '' : 's'}.`
                                    : 'Paste a valid JSON array of mint IDs.'}
                            </small>
                        </div>
                    )}

                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving…' : 'Save Gate List'}
                    </button>
                </form>

                {message && <p style={{ marginTop: '1rem' }}>{message}</p>}
            </div>
        </div>
    );
}
